import React from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { InventoryPlanningProvider, useInventoryPlanning } from '../contexts/InventoryPlanningContext';
import ArticleSelector from '../components/ArticleSelector';
import InventoryTabs, { InventoryTab } from '../components/InventoryTabs';
import OrderPanel from '../components/OrderPanel';
import StockTodayTab from '../components/tabs/StockTodayTab';
import SalesHistoryTab from '../components/tabs/SalesHistoryTab';
import ForecastTab from '../components/tabs/ForecastTab';
import CoverageTab from '../components/tabs/CoverageTab';
import SecurityRopTab from '../components/tabs/SecurityRopTab';
import ProjectedStockTab from '../components/tabs/ProjectedStockTab';
import OutstockDeclarationsTable from '../components/OutstockDeclarationsTable';
import { useToast } from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';

const CommandesInner: React.FC = () => {
  const { activeDatabase } = useDatabaseContext();
  const { data, loading, error, selectedArticleIds, setArticlesFromSelector, planningMode, setPlanningMode, applyLeftFilter, setApplyLeftFilter, applyDraftToProjections, setApplyDraftToProjections, allowNegativeStock, setAllowNegativeStock, serviceLevel, setServiceLevel, coverageMonths, setCoverageMonths } = useInventoryPlanning();
  const { toasts, removeToast } = useToast();
  // Local debounced inputs for header controls to allow rapid stepping without immediate aggregate refresh
  const [localServiceLevel, setLocalServiceLevel] = React.useState<number>(serviceLevel);
  const [localCoverageMonths, setLocalCoverageMonths] = React.useState<number>(coverageMonths);
  React.useEffect(() => { setLocalServiceLevel(serviceLevel); }, [serviceLevel]);
  React.useEffect(() => { setLocalCoverageMonths(coverageMonths); }, [coverageMonths]);
  React.useEffect(() => {
    const t = window.setTimeout(() => {
      if (Number.isFinite(localServiceLevel) && localServiceLevel > 0 && localServiceLevel <= 1) setServiceLevel(localServiceLevel);
    }, 300);
    return () => window.clearTimeout(t);
  }, [localServiceLevel, setServiceLevel]);
  React.useEffect(() => {
    const t = window.setTimeout(() => {
      const v = Math.max(1, Math.min(36, Math.round(localCoverageMonths)));
      if (v !== coverageMonths) setCoverageMonths(v);
    }, 300);
    return () => window.clearTimeout(t);
  }, [localCoverageMonths, setCoverageMonths, coverageMonths]);
  const [activeTab, setActiveTab] = React.useState<InventoryTab>('stock-today');

  if (!activeDatabase) {
    return <div className="bg-white rounded-lg shadow p-6">Sélectionnez une base de données.</div>;
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="flex-shrink-0 bg-white border-b">
        <div className="flex items-center gap-4">
          <ArticleSelector
            onSelectionChange={() => {}}
            onApplyFilters={() => {}}
            onArticlesChange={(articles) => {
              const ids = (articles || []).map((a) => String(a.id));
              setArticlesFromSelector(ids);
            }}
          />
          <div className="text-sm text-gray-600">Commandes – {selectedArticleIds.length || 'tous'} articles</div>
          {/* Global Toggles */}
          <div className="ml-4 inline-flex items-center gap-3 text-xs">
            {/* 1: SS/CB */}
            <div className="inline-flex rounded border">
              <button className={`px-3 py-1 ${planningMode === 'ss' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setPlanningMode('ss')}>SS</button>
              <button className={`px-3 py-1 border-l ${planningMode === 'cb' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setPlanningMode('cb')}>CB</button>
            </div>
            {/* Service level (SS only) */}
            {planningMode === 'ss' && (
              <label className="inline-flex items-center gap-2" title="Niveau de service global (0,90 - 0,99). Stock de sécurité calculé en conséquence.">
                <span>Service</span>
                <input
                  type="number"
                  step={0.01}
                  min={0.5}
                  max={0.999}
                  className="border rounded px-1 py-0.5 w-20"
                  value={Number(localServiceLevel)}
                  onChange={(e) => setLocalServiceLevel(Number(e.target.value || 0.95))}
                  onBlur={() => setServiceLevel(Number(localServiceLevel || 0.95))}
                />
              </label>
            )}
            {/* Coverage months (CB only) */}
            {planningMode === 'cb' && (
              <label className="inline-flex items-center gap-2" title="Nombre de mois de couverture (buffer CB) utilisé pour le calcul du point de commande.">
                <span>Couverture (mois)</span>
                <input
                  type="number"
                  min={1}
                  max={36}
                  step={1}
                  className="border rounded px-1 py-0.5 w-20"
                  value={Number(localCoverageMonths)}
                  onChange={(e) => {
                    const v = parseInt(e.target.value || '16', 10);
                    setLocalCoverageMonths(isFinite(v) ? v : 16);
                  }}
                  onBlur={() => {
                    const v = Math.max(1, Math.min(36, Math.round(localCoverageMonths)));
                    setCoverageMonths(v);
                  }}
                />
              </label>
            )}
            {/* 2: Filter left with order */}
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={applyLeftFilter} onChange={(e) => setApplyLeftFilter(e.target.checked)} />
              Filtrer avec commande
            </label>
            {/* 3: Apply order to projections */}
            <label className="inline-flex items-center gap-2" title="Appliquer les arrivées de la commande courante dans les projections (tous onglets)">
              <input type="checkbox" checked={applyDraftToProjections} onChange={(e) => setApplyDraftToProjections(e.target.checked)} />
              Appliquer aux projections
            </label>
          {/* 4: Allow negative stock */}
          <label className="inline-flex items-center gap-2" title="Autoriser le stock négatif (vente de stock non encore arrivé)">
            <input type="checkbox" checked={allowNegativeStock} onChange={(e) => setAllowNegativeStock(e.target.checked)} />
            Autoriser stock négatif
          </label>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex overflow-x-hidden">
        <div className="flex-1 min-w-0 overflow-x-auto overflow-y-auto relative">
          <InventoryTabs activeTab={activeTab} onTabChange={setActiveTab} loading={loading} />
          {/* Data Panel Tabs placeholder: Stock today, Sales history, Forecast, Coverage, Reorder months, Security stocks */}
          <div className="p-4">
            {loading ? (
              <div className="min-h-[60vh] w-full flex items-center justify-center">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="h-10 w-10 rounded-full border-2 border-blue-300 border-t-transparent animate-spin" aria-hidden="true" />
                  <span style={{fontSize: '1.25rem'}}>Chargement des données…</span>
                </div>
              </div>
            ) : error ? (
              <div className="min-h-[60vh] w-full flex items-center justify-center">
                <div className="bg-red-50 text-red-700 border border-red-200 rounded px-4 py-3 text-sm max-w-xl">
                  <div className="font-medium mb-1">Impossible de charger les données</div>
                  <div>{error}</div>
                  <div className="mt-2 text-xs text-red-600">Conseil: vérifiez que les données fournisseurs et article-fournisseurs sont bien importées.</div>
                </div>
              </div>
            ) : (
              data && (
                <div className="w-max">
                  {activeTab === 'stock-today' && <StockTodayTab />}
                  {activeTab === 'sales-history' && <SalesHistoryTab />}
                  {activeTab === 'forecast' && <ForecastTab />}
                  {activeTab === 'coverage' && <CoverageTab />}
                  {activeTab === 'projected-stock' && <ProjectedStockTab />}
                  {activeTab === 'security-rop' && <SecurityRopTab />}
                  {activeTab === 'outstock-declarations' && (
                    <div style={{minWidth: '600px'}} className="max-w-[1200px]">
                      <OutstockDeclarationsTable />
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
        <OrderPanel />
      </div>
    </div>
  );
};

const Commandes: React.FC = () => (
  <InventoryPlanningProvider>
    <CommandesInner />
  </InventoryPlanningProvider>
);

export default Commandes;


